import { useCallback, useRef } from 'react';

import { useEventListener } from '@lumapps/utils/hooks/useEventListener';

import { lumappsAnalyticsPlugin } from '../tracking';
import { TargetType, TrackingEventType } from '../types';

/**
 * This hook is tracking clicks.
 * When the tracked element (ref attached) is clicked, it will trigger a batched tracking event
 * @param targetId string
 * @param targetType TargetType
 * @param triggerOnce boolean - if true, the click will be tracked only once
 * @returns ref to attach to the tracked element
 */
export const useClickTracking = (targetId: string, targetType: TargetType, triggerOnce = false) => {
    const hasBeenClicked = useRef(false);
    const ref = useRef<HTMLElement>(null);

    const handleClick = useCallback(() => {
        if (triggerOnce && hasBeenClicked.current) {
            return;
        }

        lumappsAnalyticsPlugin.trackAndBatch({
            payload: { action: { targetId, targetType, type: TrackingEventType.CLICK_ACTION } },
        });

        if (triggerOnce) {
            hasBeenClicked.current = true;
        }
    }, [targetId, targetType, triggerOnce]);

    useEventListener('click', handleClick, ref);

    return ref;
};
